import React, { Fragment, useContext, useEffect, useMemo, useState } from "react";
import * as Io from "react-icons/io";
import * as Md from "react-icons/md";
import TodaysEnquiries, {
  toDateKey,
  mergeById,
  matchesStatusFilter,
} from "./TodaysEnquiries";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useLocation } from "react-router-dom";
import { AppContext } from "../../context/AppContext";
import PendingEnquiries from "./PendingEnquiries";
import UpcomingEnquiries from "./UpcomingEnquiries";
// import ConfirmedEnquiries from "./ConfirmedEnquiries";
// import CancelledEnquiries from "./CancelledEnquiries";
import EnquiriesHistory from "./EnquiriesHistory";
import "../../styles/style.css";

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "Approved", label: "Confirmed" },
  { id: "Seated", label: "Seated" },
  { id: "needs_approval", label: "Needs approval" },
];

const HISTORY_STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "Completed", label: "Completed" },
  { id: "Cancelled", label: "Cancelled" },
  { id: "Rejected", label: "Rejected" },
  { id: "Expired", label: "Expired" },
];

function pad(n) {
  return String(n).padStart(2, "0");
}

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function shiftDate(dateStr, deltaDays) {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + deltaDays);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatNavDateLabel(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  const weekday = d.toLocaleDateString("en-GB", { weekday: "short" });
  const day = d.getDate();
  const month = d.toLocaleDateString("en-GB", { month: "short" });
  const isToday = dateStr === todayKey();
  return isToday
    ? `Today, ${weekday} ${day} ${month}`
    : `${weekday} ${day} ${month}`;
}

function getActiveTab(search) {
  const tab = new URLSearchParams(search).get("tab");
  if (tab == null || tab === "") return 0;
  const parsed = parseInt(tab, 10);
  if (Number.isNaN(parsed) || parsed < 0 || parsed > 3) return 0;
  return parsed;
}

function EnquiriesList() {
  const location = useLocation();
  const {
    recentEnquiries,
    todayEnquiries,
    upcomingEnquiries,
    enquiriesHistory,
    reservationLoading,
    fetchNewReservations,
    fetchReservationHistory,
    fetchFutureReservations,
    todaysReservationList,
    upcomingwithtodaysReservationList,
    fetchAllTables,
  } = useContext(AppContext);
  const [pendingList, setpendingList] = useState(null);
  const [searchWord, setSearchWord] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [selectedDate, setSelectedDate] = useState(todayKey);
  const [statusFilter, setStatusFilter] = useState("all");

  const activeTab = useMemo(
    () => getActiveTab(location.search),
    [location.search]
  );

  useEffect(() => {
    filterEnquiries();
  }, [recentEnquiries, todayEnquiries, upcomingEnquiries]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const dateParam =
      queryParams.get("date") || sessionStorage.getItem("enquirySearchDate");

    if (dateParam) {
      if (activeTab === 0) {
        setSelectedDate(dateParam);
      } else {
        setSearchDate(dateParam);
      }
      sessionStorage.removeItem("enquirySearchDate");
    }

    setStatusFilter("all");
    if (activeTab === 0) {
      setSearchDate("");
      setSearchWord("");
    }
  }, [location.search, activeTab]);

  const fetchAllBookinGdetails = async () => {
    const shopToken = window.localStorage.getItem("shopCustomToken");
    if (!shopToken) return;

    await fetchNewReservations(shopToken);
    await todaysReservationList(shopToken);
    await fetchReservationHistory(shopToken);
    await fetchFutureReservations(shopToken);
    await upcomingwithtodaysReservationList(shopToken);
    await fetchAllTables(shopToken);
  };

  const mergeLists = (...lists) => {
    const byId = new Map();
    lists.forEach((list) => {
      if (!Array.isArray(list)) return;
      list.forEach((item) => {
        if (item?.id == null) return;
        byId.set(String(item.id), item);
      });
    });
    return Array.from(byId.values());
  };

  const filterEnquiries = () => {
    const baseEnquiries = Array.isArray(recentEnquiries?.enquiries)
      ? recentEnquiries.enquiries
      : [];
    const modified = Array.isArray(recentEnquiries?.modified_enquiries)
      ? recentEnquiries.modified_enquiries
      : [];

    const mergedPendingSource = mergeLists(baseEnquiries, modified).sort(
      (a, b) => Number(a.id) - Number(b.id)
    );

    const pendings = mergedPendingSource
      .filter(
        (data) =>
          data?.status === "Requested" || data?.status === "Modified"
      )
      .reverse();

    setpendingList(pendings);
  };

  const daySourceList = useMemo(() => {
    return mergeById(
      todayEnquiries,
      upcomingEnquiries,
      enquiriesHistory,
      recentEnquiries?.enquiries,
      recentEnquiries?.modified_enquiries
    );
  }, [todayEnquiries, upcomingEnquiries, enquiriesHistory, recentEnquiries]);

  const dayList = useMemo(() => {
    return daySourceList.filter(
      (item) => toDateKey(item?.bookingTime) === selectedDate
    );
  }, [daySourceList, selectedDate]);

  const dayStats = useMemo(() => {
    const bookings = dayList.length;
    const guests = dayList.reduce(
      (sum, item) => sum + (Number(item?.chairs) || 0),
      0
    );
    const seated = dayList.filter((item) => item?.status === "Seated").length;
    return { bookings, guests, seated };
  }, [dayList]);

  const filterCounts = useMemo(() => {
    return {
      all: dayList.length,
      Approved: dayList.filter((item) => item?.status === "Approved").length,
      Seated: dayList.filter((item) => item?.status === "Seated").length,
      needs_approval: dayList.filter((item) =>
        matchesStatusFilter(item, "needs_approval")
      ).length,
    };
  }, [dayList]);

  const upcomingBaseList = useMemo(() => {
    const list = Array.isArray(upcomingEnquiries) ? upcomingEnquiries : [];
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return list.filter((item) => {
      const status = item?.status;
      if (
        status === "Cancelled" ||
        status === "Rejected" ||
        status === "Completed" ||
        status === "Expired"
      ) {
        return false;
      }
      const raw = String(item?.bookingTime || "").trim();
      if (!raw) return false;
      const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
      const bookingDate = new Date(normalized);
      if (Number.isNaN(bookingDate.getTime())) return false;
      return bookingDate >= tomorrow;
    });
  }, [upcomingEnquiries]);

  const upcomingFilterSource = useMemo(() => {
    if (!searchDate) return upcomingBaseList;
    return upcomingBaseList.filter(
      (item) => toDateKey(item?.bookingTime) === searchDate
    );
  }, [upcomingBaseList, searchDate]);

  const upcomingFilterCounts = useMemo(() => {
    return {
      all: upcomingFilterSource.length,
      Approved: upcomingFilterSource.filter(
        (item) => item?.status === "Approved"
      ).length,
      Seated: upcomingFilterSource.filter((item) => item?.status === "Seated")
        .length,
      needs_approval: upcomingFilterSource.filter((item) =>
        matchesStatusFilter(item, "needs_approval")
      ).length,
    };
  }, [upcomingFilterSource]);

  const historyFilterSource = useMemo(() => {
    const list = Array.isArray(enquiriesHistory) ? enquiriesHistory : [];
    if (!searchDate) return list;
    return list.filter((item) => toDateKey(item?.bookingTime) === searchDate);
  }, [enquiriesHistory, searchDate]);

  const historyFilterCounts = useMemo(() => {
    return {
      all: historyFilterSource.length,
      Completed: historyFilterSource.filter(
        (item) => item?.status === "Completed"
      ).length,
      Cancelled: historyFilterSource.filter(
        (item) => item?.status === "Cancelled"
      ).length,
      Rejected: historyFilterSource.filter(
        (item) => item?.status === "Rejected"
      ).length,
      Expired: historyFilterSource.filter((item) => item?.status === "Expired")
        .length,
    };
  }, [historyFilterSource]);

  const openDatePicker = (e) => {
    const input = e.currentTarget;
    if (typeof input.showPicker === "function") {
      try {
        input.showPicker();
      } catch {
        /* ignore */
      }
    }
  };

  const otherDateLabel = searchDate
    ? new Date(`${searchDate}T00:00:00`).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "All dates";

  const refreshBtn = (
    <button
      type="button"
      className={
        !reservationLoading
          ? "el-refresh refresh_btn"
          : "el-refresh refresh_btn rotate"
      }
      disabled={reservationLoading}
      onClick={fetchAllBookinGdetails}
      aria-label="Refresh"
    >
      <Io.IoMdRefresh />
      <span className="el-refresh__label">Refresh</span>
    </button>
  );

  return (
    <Fragment>
      <div className="card h-100 radius-12 enquiries-list">
        <div className="card-body p-24 enquiries-list__body">
          {activeTab === 0 ? (
            <div className="el-guest-top">
              <div className="el-date-nav">
                <button
                  type="button"
                  className="el-date-nav__arrow"
                  aria-label="Previous day"
                  onClick={() => {
                    setSelectedDate((prev) => shiftDate(prev, -1));
                    setStatusFilter("all");
                  }}
                >
                  <Io.IoIosArrowBack />
                </button>
                <label className="el-date-nav__center">
                  <Md.MdOutlineCalendarToday
                    className="el-date-nav__icon"
                    aria-hidden
                  />
                  <span className="el-date-nav__label">
                    {formatNavDateLabel(selectedDate)}
                  </span>
                  <input
                    type="date"
                    className="el-date-nav__input"
                    value={selectedDate}
                    onChange={(e) => {
                      setSelectedDate(e.target.value || todayKey());
                      setStatusFilter("all");
                    }}
                    onClick={openDatePicker}
                    aria-label="Select date"
                  />
                </label>
                <button
                  type="button"
                  className="el-date-nav__arrow"
                  aria-label="Next day"
                  onClick={() => {
                    setSelectedDate((prev) => shiftDate(prev, 1));
                    setStatusFilter("all");
                  }}
                >
                  <Io.IoIosArrowForward />
                </button>
              </div>

              <div className="el-stats" aria-label="Day summary">
                <div className="el-stats__card">
                  <strong className="el-stats__value">{dayStats.bookings}</strong>
                  <span className="el-stats__label">Bookings</span>
                </div>
                <div className="el-stats__card">
                  <strong className="el-stats__value">{dayStats.guests}</strong>
                  <span className="el-stats__label">Guests expected</span>
                </div>
                <div className="el-stats__card">
                  <strong className="el-stats__value">{dayStats.seated}</strong>
                  <span className="el-stats__label">Seated now</span>
                </div>
              </div>

              <div className="el-guest-top__tools">
                <form
                  className="el-search navbar-search position-relative"
                  id="item-search"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <Icon
                    icon="ion:search-outline"
                    className="icon el-search__icon"
                  />
                  <input
                    type="text"
                    className="form-control el-search__input"
                    name="search"
                    placeholder="Search by name or booking reference"
                    onChange={(e) => setSearchWord(e.target.value)}
                    value={searchWord}
                  />
                  {searchWord && searchWord.length != 0 && (
                    <i
                      className="close_sm_btn"
                      onClick={() => setSearchWord("")}
                    >
                      <Io.IoMdCloseCircle />
                    </i>
                  )}
                </form>

                <div
                  className="today-status-filters"
                  role="tablist"
                  aria-label="Filter reservations by status"
                >
                  {STATUS_FILTERS.map((filter) => {
                    const count = filterCounts[filter.id] ?? 0;
                    const isActive = statusFilter === filter.id;
                    return (
                      <button
                        key={filter.id}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={
                          isActive
                            ? "today-status-filters__btn is-active"
                            : "today-status-filters__btn"
                        }
                        onClick={() => setStatusFilter(filter.id)}
                      >
                        <span>
                          {filter.label}
                          {filter.id === "all" ? ` · ${count}` : ""}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="el-guest-heading">
                <div className="el-guest-heading__text">
                  <h2 className="el-guest-heading__title">Your guest list</h2>
                  <p className="el-guest-heading__count">
                    (
                    {statusFilter === "all"
                      ? dayStats.bookings
                      : (filterCounts[statusFilter] ?? 0)}{" "}
                    {(statusFilter === "all"
                      ? dayStats.bookings
                      : (filterCounts[statusFilter] ?? 0)) === 1
                      ? "booking"
                      : "bookings"}
                    )
                  </p>
                </div>
                {refreshBtn}
              </div>
            </div>
          ) : (
            <div className="el-toolbar">
              <form
                className="el-search navbar-search position-relative"
                id="item-search"
                onSubmit={(e) => e.preventDefault()}
              >
                <Icon
                  icon="ion:search-outline"
                  className="icon el-search__icon"
                />
                <input
                  type="text"
                  className="form-control el-search__input"
                  name="search"
                  placeholder="Name, email, phone or ID"
                  onChange={(e) => setSearchWord(e.target.value)}
                  value={searchWord}
                />
                {searchWord && searchWord.length != 0 && (
                  <i
                    className="close_sm_btn"
                    onClick={() => setSearchWord("")}
                  >
                    <Io.IoMdCloseCircle />
                  </i>
                )}
              </form>

              <label className="el-date">
                <Md.MdOutlineCalendarToday
                  className="el-date__icon"
                  aria-hidden
                />
                <span className="el-date__label">{otherDateLabel}</span>
                <input
                  type="date"
                  className="el-date__input"
                  onChange={(e) => setSearchDate(e.target.value)}
                  onClick={openDatePicker}
                  value={searchDate}
                  aria-label="Filter by date"
                />
                {searchDate ? (
                  <button
                    type="button"
                    className="el-date__clear"
                    aria-label="Clear date"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSearchDate("");
                    }}
                  >
                    <Io.IoMdClose />
                  </button>
                ) : (
                  <Io.IoIosArrowDown
                    className="el-date__chevron"
                    aria-hidden
                  />
                )}
              </label>

              {refreshBtn}
            </div>
          )}

          {activeTab === 2 && (
            <div
              className="today-status-filters el-upcoming-filters"
              role="tablist"
              aria-label="Filter upcoming reservations by status"
            >
              {STATUS_FILTERS.map((filter) => {
                const count = upcomingFilterCounts[filter.id] ?? 0;
                const isActive = statusFilter === filter.id;
                return (
                  <button
                    key={filter.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={
                      isActive
                        ? "today-status-filters__btn is-active"
                        : "today-status-filters__btn"
                    }
                    onClick={() => setStatusFilter(filter.id)}
                  >
                    <span>
                      {filter.label}
                      {filter.id === "all" ? ` · ${count}` : ""}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {activeTab === 3 && (
            <div
              className="today-status-filters el-upcoming-filters"
              role="tablist"
              aria-label="Filter history by status"
            >
              {HISTORY_STATUS_FILTERS.map((filter) => {
                const count = historyFilterCounts[filter.id] ?? 0;
                const isActive = statusFilter === filter.id;
                return (
                  <button
                    key={filter.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={
                      isActive
                        ? "today-status-filters__btn is-active"
                        : "today-status-filters__btn"
                    }
                    onClick={() => setStatusFilter(filter.id)}
                  >
                    <span>
                      {filter.label}
                      {filter.id === "all" ? ` · ${count}` : ""}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="enquiries-list__content">
            {activeTab === 0 && (
              <TodaysEnquiries
                keyword={searchWord}
                selectedDate={selectedDate}
                statusFilter={statusFilter}
              />
            )}
            {activeTab === 1 && (
              <PendingEnquiries
                pendingList={pendingList}
                activeTab={activeTab}
                keyword={searchWord}
                searchDate={searchDate}
                setSearchWord={setSearchWord}
              />
            )}
            {activeTab === 2 && (
              <UpcomingEnquiries
                keyword={searchWord}
                searchDate={searchDate}
                statusFilter={statusFilter}
              />
            )}
            {/* {activeTab === "approved" && (
              <ConfirmedEnquiries
                approvedList={confirmedList}
                keyword={searchWord}
                searchDate={searchDate}
              />
            )}
            {activeTab === "cancelled" && (
              <CancelledEnquiries
                cancelledEnquiries={cancelledEnquiries}
                keyword={searchWord}
                searchDate={searchDate}
              />
            )} */}
            {activeTab === 3 && (
              <EnquiriesHistory
                keyword={searchWord}
                searchDate={searchDate}
                statusFilter={statusFilter}
              />
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export default EnquiriesList;
